// @flow

import React, { Component } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { localize } from "redux-i18n";

import sharedActionCreators from "@modules/shared/actionCreator";
import * as sharedSelectors from "@modules/shared/selectors";
import type { HierarchyTree } from "@modules/shared/reducer/hierarchy/types";
import type { ApprovableContainer } from "@modules/service/types";
import type { Alert } from "@modules/service/reducer/alerts/types";
import * as accountSelectors from "@modules/account/selectors";
import * as serviceSelectors from "@modules/service/selectors";
import actionCreators from "@modules/service/actionCreator";

import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from "@components";
import { HierarchyScopeForm } from "@modules/service/components/hierarchy-scope";
import type { HierarchyAssignment } from "@modules/service/types";

type PropsFromState = $Exact<{
  current: ?ApprovableContainer<Alert>,
  hierarchy: ?HierarchyTree,
  draftAssignments: Array<HierarchyAssignment>,
  isAlertDraftAssignmentsValid: boolean,
}>;

type PropsFromDispatch = $Exact<{
  setAssignments: typeof actionCreators.SetAlertDraftAssignments,
  updateAssignments: typeof actionCreators.UpdateAlertAssignments,
}>;

type OwnProps = $Exact<{
  clientId: number,
  onCancel: () => void,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

export class AlertsEditAssignmentsModal extends Component<Props> {
  handleCancel = () => {
    this.props.setAssignments([]);
    this.props.onCancel();
  };

  handleSubmitAlertDraft = () => {
    if (this.props.current && this.props.current.hierarchyAssignments) {
      this.props.updateAssignments({
        container: this.props.current,
        assignments: this.props.draftAssignments,
      });
      this.handleCancel();
    }
  };

  handleChanges = (data: Array<HierarchyAssignment>) => {
    this.props.setAssignments(data);
  };

  render() {
    return (
      <Modal className="modal-large">
        <ModalHeader className="d-flex justify-content-between" toggle={this.handleCancel}>
          {this.props.t("ALERT_EDIT_HIERARCHY_SCOPE", {
            ...(this.props.current ? { alertId: this.props.current.id } : {}),
          })}
        </ModalHeader>
        <ModalBody className="d-flex flex-column align-content-center">
          <div className="w-100 d-flex justify-content-center">
            <div className="col-sm-12">
              {this.props.hierarchy && this.props.draftAssignments ? (
                <HierarchyScopeForm
                  hierarchy={this.props.hierarchy}
                  assignments={this.props.draftAssignments}
                  onChange={this.handleChanges}
                />
              ) : null}
            </div>
          </div>
        </ModalBody>
        <ModalFooter className="d-flex justify-content-end">
          <Button color="secondary" onClick={this.handleCancel}>
            {this.props.t("CANCEL")}
          </Button>
          <Button
            materialIcon="replay_circle_filled"
            onClick={this.handleSubmitAlertDraft}
            disabled={!this.props.isAlertDraftAssignmentsValid}
          >
            {this.props.t("SUBMIT")}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
}

const mapStateToProps = (state, ownProps): PropsFromState => {
  return {
    current: serviceSelectors.getCurrentAlert(state),
    hierarchy: sharedSelectors.getHierarchyTree(state),
    draftAssignments: serviceSelectors.getAlertDraftAssignments(state),
    isAlertDraftAssignmentsValid: serviceSelectors.isAlertDraftAssignmentsValid(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  setAssignments: actionCreators.SetAlertDraftAssignments,
  updateAssignments: actionCreators.UpdateAlertAssignments,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(AlertsEditAssignmentsModal);
